import { Directive } from '@angular/core';

/**
 * Base directive for list items.
 * Applied to elements with the `ds-list-item` attribute.
 */
@Directive({
    selector: '[ds-list-item]',
    standalone: true,
})
export class DsListItemDirective {}

/**
 * Directive for list item titles.
 * Applies consistent styling to title elements within list items.
 *
 * Supports multiple selectors for backwards compatibility:
 * - `ds-list-item-title` - Standard list item titles
 * - `ds-list-option-title` - List option titles
 * - `ce-dropdown-option-title` - Context menu/dropdown option titles
 */
@Directive({
    selector: '[ds-list-item-title],[ds-list-option-title],[ce-dropdown-option-title]',
    host: {
        class: 'u-spacing-none-pa u-spacing-sm-mr',
    },
    standalone: true,
})
export class DsListItemTitleDirective {}

/**
 * Directive for list item prefix content.
 * Used to display icons, avatars, or other leading content before the list item text.
 *
 * Supports multiple selectors for backwards compatibility:
 * - `ds-list-item-prefix` - Standard list item prefix
 * - `ds-list-option-prefix` - List option prefix
 * - `ce-dropdown-option-prefix` - Context menu/dropdown option prefix
 */
@Directive({
    selector: '[ds-list-item-prefix],[ce-dropdown-option-prefix],[ds-list-option-prefix]',
    standalone: true,
    host: {
        class: 'u-spacing-none-pa u-spacing-sm-mr',
    },
})
export class DsListItemPrefixDirective {}

/**
 * Directive for list item dividers.
 * Creates a horizontal rule to visually separate groups of list items.
 *
 * Must be applied to `<hr>` elements.
 * Sets aria-hidden and removes from tab order for accessibility.
 */
@Directive({
    selector: 'hr[ds-list-item-divider],hr[ds-list-option-divider]',
    standalone: true,
    host: {
        style: 'border: none; border-top: 1px solid var(--color-border-secondary); margin: 0; height: 0;',
        '[attr.aria-hidden]': '"true"',
        '[attr.tabindex]': 'null',
    },
})
export class DsListItemDividerDirective {}

/**
 * Directive for list item suffix content.
 * Used to display badges, icons, shortcuts, or other trailing content after the list item text.
 *
 * Supports multiple selectors for backwards compatibility:
 * - `ds-list-item-suffix` - Standard list item suffix
 * - `ds-list-option-suffix` - List option suffix
 * - `ce-dropdown-option-suffix` - Context menu/dropdown option suffix
 */
@Directive({
    selector: '[ds-list-item-suffix],[ce-dropdown-option-suffix],[ds-list-option-suffix]',
    standalone: true,
    host: {
        class: 'flex-end u-spacing-none-pa',
    },
})
export class DsListItemSuffixDirective {}

/**
 * Directive to mark a list item as disabled.
 * Automatically sets the `disabled` attribute on the host element.
 *
 * Supports both list items and list options:
 * - `ds-list-item-disabled` - For standard list items
 * - `ds-list-option-disabled` - For selectable list options
 */
@Directive({
    selector: '[ds-list-item-disabled],[ds-list-option-disabled]',
    standalone: true,
    host: {
        '[attr.disabled]': '"true"',
    },
})
export class DsListItemDisabledDirective {}

/**
 * Directive for list item descriptions.
 * Displays secondary text below the main title with reduced emphasis.
 *
 * Applies styling for:
 * - Small body text
 * - Secondary text color
 *
 * Supports multiple selectors for backwards compatibility:
 * - `ds-list-item-description` - Standard list item description
 * - `ds-list-option-description` - List option description
 * - `ce-dropdown-option-description` - Context menu/dropdown option description
 */
@Directive({
    selector: '[ds-list-item-description],[ds-list-option-description],[ce-dropdown-option-description]',
    standalone: true,
    host: {
        class: 'u-text-body-sm u-color-text-secondary',
    },
})
export class DsListItemDescriptionDirective {}
